import { get, writable } from 'svelte/store';
import { validate_ip } from 'src/helpers/validators';
import { wifi_ssid } from '../session/native-bridge';

/** How long a probed local IP remains trusted before `get_local_ip` falls back to status/scan. */
export const VERIFIED_LOCAL_IP_TTL_MS = 5 * 60 * 1000;

/** Cached LAN IP entry written after a successful multi-interface probe. */
export type VerifiedLocalIp = {
	ip: string;
	verifiedAt: number;
};

/**
 * Map of `real_id` → last verified LAN IP from a multi-interface probe.
 * Read via {@link get_fresh_verified_local_ip} inside `get_local_ip`.
 */
export const verified_local_ips = writable<Record<string, VerifiedLocalIp>>({});

let lastWifiSsid: string | undefined = get(wifi_ssid);

wifi_ssid.subscribe((ssid) => {
	if (lastWifiSsid !== undefined && lastWifiSsid !== ssid) {
		clear_verified_local_ips();
	}
	lastWifiSsid = ssid;
});

/** Clears all verified IPs (e.g. on Wi‑Fi network change or leaving LAN). */
export const clear_verified_local_ips = () => {
	verified_local_ips.set({});
};

/**
 * Stores a probed LAN IP for use by `get_local_ip`.
 *
 * @param realId - Device real id (no channel suffix)
 * @param ip - Verified IPv4 address
 */
export const set_verified_local_ip = (realId: string, ip: string) => {
	if (!validate_ip(ip)) return;
	verified_local_ips.update((entries) => ({
		...entries,
		[realId]: { ip, verifiedAt: Date.now() },
	}));
};

/**
 * Returns a cached verified IP when still within {@link VERIFIED_LOCAL_IP_TTL_MS}.
 *
 * @param realId - Device real id (no channel suffix)
 * @returns Fresh verified IP, or `false` when missing or expired
 */
export const get_fresh_verified_local_ip = (realId: string): string | false => {
	const entry = get(verified_local_ips)[realId];
	if (!entry) return false;
	if (Date.now() - entry.verifiedAt > VERIFIED_LOCAL_IP_TTL_MS) return false;
	return validate_ip(entry.ip) ? entry.ip : false;
};
